import { useState, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import type { Driver } from '@/types/rezervasyon';

interface SettingMap {
  firma_adi: string;
  varsayilan_para_birimi: string;
  fiyat_carpan: string;
  gece_carpan: string;
  cocuk_koltuk_ucreti: string;
}

const DEFAULT_SETTINGS: SettingMap = {
  firma_adi: 'VIP Antalya Transfer',
  varsayilan_para_birimi: 'EUR',
  fiyat_carpan: '1.0',
  gece_carpan: '1.2',
  cocuk_koltuk_ucreti: '10',
};

const TABS = [
  { id: 'genel', label: 'Genel', icon: 'ri-settings-line' },
  { id: 'fiyat', label: 'Fiyatlandırma', icon: 'ri-price-tag-3-line' },
  { id: 'sofor', label: 'Şoförler', icon: 'ri-steering-line' },
];

export default function AyarlarPage() {
  useAuthGuard();
  const [aktifSekme, setAktifSekme] = useState('genel');
  const [settings, setSettings] = useState<SettingMap>(DEFAULT_SETTINGS);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  // New driver form
  const [yeniSofor, setYeniSofor] = useState({ ad_soyad: '', telefon: '', lisans_no: '', maas: '', maas_para_birimi: 'EUR' });
  const [addingDriver, setAddingDriver] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [editDriver, setEditDriver] = useState<Driver | null>(null);
  const [deleteDriverConfirm, setDeleteDriverConfirm] = useState<Driver | null>(null);
  const [deletingDriver, setDeletingDriver] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [sRes, dRes] = await Promise.all([
      supabase.from('settings').select('key,value'),
      supabase.from('drivers').select('*').order('ad_soyad'),
    ]);
    if (sRes.data) {
      const map: Record<string, string> = {};
      sRes.data.forEach((s: { key: string; value: string }) => { map[s.key] = s.value; });
      setSettings({ ...DEFAULT_SETTINGS, ...map } as SettingMap);
    }
    if (dRes.data) setDrivers(dRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const handleSaveSettings = async () => {
    setSaving(true);
    const entries = Object.entries(settings);
    for (const [key, value] of entries) {
      await supabase.from('settings').upsert({ key, value }, { onConflict: 'key' });
    }
    setSaving(false);
    showSuccess('Ayarlar kaydedildi.');
  };

  const handleAddDriver = async () => {
    if (!yeniSofor.ad_soyad.trim()) return;
    setAddingDriver(true);
    setError('');
    const { data, error: err } = await supabase.functions.invoke('manage-data', {
      body: {
        action: 'insert',
        table: 'drivers',
        data: {
          ad_soyad: yeniSofor.ad_soyad,
          telefon: yeniSofor.telefon,
          lisans_no: yeniSofor.lisans_no,
          aktif: true,
          maas: yeniSofor.maas ? parseFloat(yeniSofor.maas) : 0,
          maas_para_birimi: yeniSofor.maas_para_birimi,
        },
      },
    });
    if (err || !data?.success) {
      setError(err?.message || data?.error || 'Şoför eklenemedi');
    } else if (data?.data) {
      setDrivers(prev => [...prev, data.data]);
      setYeniSofor({ ad_soyad: '', telefon: '', lisans_no: '', maas: '', maas_para_birimi: 'EUR' });
      setShowAddDriver(false);
      showSuccess('Şoför eklendi.');
    }
    setAddingDriver(false);
  };

  const handleUpdateDriver = async () => {
    if (!editDriver) return;
    setSaving(true);
    setError('');
    const { data, error: err } = await supabase.functions.invoke('manage-data', {
      body: {
        action: 'update',
        table: 'drivers',
        id: editDriver.id,
        data: {
          ad_soyad: editDriver.ad_soyad,
          telefon: editDriver.telefon,
          lisans_no: editDriver.lisans_no,
          aktif: editDriver.aktif,
          maas: editDriver.maas,
          maas_para_birimi: editDriver.maas_para_birimi,
        },
      },
    });
    if (!err && data?.success) {
      setDrivers(prev => prev.map(d => (d.id === editDriver.id ? data.data : d)));
      setEditDriver(null);
      showSuccess('Şoför güncellendi.');
    } else {
      setError(err?.message || data?.error || 'Güncellenemedi');
    }
    setSaving(false);
  };

  const handleDeleteDriver = async () => {
    if (!deleteDriverConfirm) return;
    const idToDelete = deleteDriverConfirm.id;
    setDeletingDriver(true);
    setDeleteDriverConfirm(null);
    setError('');

    const { data, error } = await supabase.functions.invoke('manage-data', {
      body: { action: 'delete', table: 'drivers', id: idToDelete },
    });

    setDeletingDriver(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setDrivers(prev => prev.filter(d => d.id !== idToDelete));
    showSuccess('Şoför silindi.');
  };

  const toggleDriverAktif = async (driver: Driver) => {
    setError('');
    const { data, error: err } = await supabase.functions.invoke('manage-data', {
      body: { action: 'update', table: 'drivers', id: driver.id, data: { aktif: !driver.aktif } },
    });
    if (!err && data?.success) {
      setDrivers(prev => prev.map(d => (d.id === driver.id ? { ...d, aktif: !d.aktif } : d)));
    } else {
      setError(err?.message || data?.error || 'Aktif durumu güncellenemedi');
    }
  };

  return (
    <AdminLayout>
      {/* Delete Driver Confirm */}
      {deleteDriverConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-semibold text-white text-center mb-2">Şoförü Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-5"><strong className="text-white">{deleteDriverConfirm.ad_soyad}</strong></p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteDriverConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
              <button onClick={handleDeleteDriver} disabled={deletingDriver} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm disabled:opacity-50 whitespace-nowrap">
                {deletingDriver ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">Ayarlar</h2>
        <p className="text-sm text-gray-400 mt-1">Sistem yapılandırması ve operasyonel ayarlar</p>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{error}<button onClick={() => setError('')} className="ml-auto"><i className="ri-close-line"></i></button></div>}
      {successMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-emerald-400 flex items-center gap-2"><i className="ri-check-line"></i>{successMsg}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-vip-dark border border-vip-gold/10 rounded-lg p-1 mb-6 overflow-x-auto w-fit">
        {TABS.map(s => (
          <button key={s.id} onClick={() => setAktifSekme(s.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${aktifSekme === s.id ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={s.icon}></i></div>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-6 animate-pulse space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-vip-surface rounded-md"></div>)}
        </div>
      ) : (
        <>
          {/* Genel */}
          {aktifSekme === 'genel' && (
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 space-y-5 max-w-2xl">
              <h3 className="text-sm font-semibold text-white">Genel Ayarlar</h3>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Firma Adı</label>
                <input value={settings.firma_adi} onChange={e => setSettings(p => ({ ...p, firma_adi: e.target.value }))}
                  className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Varsayılan Para Birimi</label>
                <select value={settings.varsayilan_para_birimi} onChange={e => setSettings(p => ({ ...p, varsayilan_para_birimi: e.target.value }))}
                  className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                  {['EUR','USD','TL'].map(pb => <option key={pb} value={pb}>{pb}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Fiyatlandırma */}
          {aktifSekme === 'fiyat' && (
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 space-y-5 max-w-2xl">
              <h3 className="text-sm font-semibold text-white">Fiyatlandırma Kuralları</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Genel Fiyat Çarpanı</label>
                  <input type="number" step="0.1" value={settings.fiyat_carpan} onChange={e => setSettings(p => ({ ...p, fiyat_carpan: e.target.value }))}
                    className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
                  <p className="text-[10px] text-gray-500 mt-1">Tüm fiyatlar bu çarpan ile çarpılır</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Gece (22:00-06:00) Çarpanı</label>
                  <input type="number" step="0.1" value={settings.gece_carpan} onChange={e => setSettings(p => ({ ...p, gece_carpan: e.target.value }))}
                    className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
                  <p className="text-[10px] text-gray-500 mt-1">Gece transferleri için ek çarpan</p>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Çocuk Koltuğu Ücreti (EUR/adet)</label>
                <input type="number" value={settings.cocuk_koltuk_ucreti} onChange={e => setSettings(p => ({ ...p, cocuk_koltuk_ucreti: e.target.value }))}
                  className="w-32 bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
              </div>
            </div>
          )}

          {/* Şoförler */}
          {aktifSekme === 'sofor' && (
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 max-w-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white">Şoför Listesi ({drivers.length})</h3>
                <button onClick={() => setShowAddDriver(!showAddDriver)}
                  className="px-3 py-1.5 bg-vip-gold/15 text-vip-gold text-xs font-medium rounded-md border border-vip-gold/20 hover:bg-vip-gold/25 transition-colors whitespace-nowrap">
                  <i className="ri-add-line mr-1"></i>Yeni Şoför
                </button>
              </div>

              {showAddDriver && (
                <div className="bg-vip-black/50 border border-vip-gold/10 rounded-lg p-4 mb-4 space-y-3">
                  <p className="text-xs font-medium text-vip-gold">Yeni Şoför Ekle</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={yeniSofor.ad_soyad} onChange={e => setYeniSofor(p => ({ ...p, ad_soyad: e.target.value }))}
                      placeholder="Ad Soyad *" className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
                    <input value={yeniSofor.telefon} onChange={e => setYeniSofor(p => ({ ...p, telefon: e.target.value }))}
                      placeholder="Telefon" className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
                    <input value={yeniSofor.lisans_no} onChange={e => setYeniSofor(p => ({ ...p, lisans_no: e.target.value }))}
                      placeholder="Lisans No" className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
                    <div className="flex gap-2">
                      <input type="number" value={yeniSofor.maas} onChange={e => setYeniSofor(p => ({ ...p, maas: e.target.value }))}
                        placeholder="Maaş" className="flex-1 bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
                      <select value={yeniSofor.maas_para_birimi} onChange={e => setYeniSofor(p => ({ ...p, maas_para_birimi: e.target.value }))}
                        className="w-20 bg-vip-dark border border-vip-gold/15 rounded-md px-2 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                        {['EUR','USD','TL','GBP'].map(pb => <option key={pb} value={pb}>{pb}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddDriver(false)} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors whitespace-nowrap">İptal</button>
                    <button onClick={handleAddDriver} disabled={addingDriver || !yeniSofor.ad_soyad.trim()}
                      className="px-4 py-1.5 bg-vip-gold text-vip-black text-xs font-semibold rounded-md hover:bg-vip-gold-light transition-colors disabled:opacity-50 whitespace-nowrap">
                      {addingDriver ? 'Ekleniyor...' : 'Ekle'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {drivers.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-3 py-2.5 px-3 bg-vip-black/50 rounded-md">
                    {editDriver?.id === d.id ? (
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input value={editDriver.ad_soyad} onChange={e => setEditDriver(p => p ? { ...p, ad_soyad: e.target.value } : p)}
                          className="bg-vip-dark border border-vip-gold/30 rounded px-2 py-1 text-xs text-white focus:outline-none" />
                        <div className="flex gap-2">
                          <input value={editDriver.telefon || ''} onChange={e => setEditDriver(p => p ? { ...p, telefon: e.target.value } : p)}
                            placeholder="Telefon" className="flex-1 bg-vip-dark border border-vip-gold/30 rounded px-2 py-1 text-xs text-white focus:outline-none" />
                        </div>
                        <input value={editDriver.lisans_no || ''} onChange={e => setEditDriver(p => p ? { ...p, lisans_no: e.target.value } : p)}
                          placeholder="Lisans No" className="bg-vip-dark border border-vip-gold/30 rounded px-2 py-1 text-xs text-white focus:outline-none" />
                        <div className="flex gap-2">
                          <input type="number" value={editDriver.maas || ''} onChange={e => setEditDriver(p => p ? { ...p, maas: parseFloat(e.target.value) || 0 } : p)}
                            placeholder="Maaş" className="flex-1 bg-vip-dark border border-vip-gold/30 rounded px-2 py-1 text-xs text-white focus:outline-none" />
                          <select value={editDriver.maas_para_birimi} onChange={e => setEditDriver(p => p ? { ...p, maas_para_birimi: e.target.value } : p)}
                            className="w-16 bg-vip-dark border border-vip-gold/30 rounded px-1 py-1 text-xs text-white focus:outline-none">
                            {['EUR','USD','TL','GBP'].map(pb => <option key={pb} value={pb}>{pb}</option>)}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="w-6 h-6 rounded-full bg-vip-gold/15 flex items-center justify-center text-[10px] text-vip-gold font-semibold">{i + 1}</span>
                        <div className="flex-1">
                          <p className="text-sm text-white">{d.ad_soyad}</p>
                          <p className="text-xs text-gray-500">{d.telefon} {d.lisans_no && `· ${d.lisans_no}`} {d.maas > 0 && `· Maaş: ${d.maas} ${d.maas_para_birimi}`}</p>
                        </div>
                        <button onClick={() => toggleDriverAktif(d)}
                          className={`text-[10px] px-2 py-0.5 rounded border ${d.aktif ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-gray-500/10 text-gray-500 border-gray-500/20'}`}>
                          {d.aktif ? 'Aktif' : 'Pasif'}
                        </button>
                      </>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {editDriver?.id === d.id ? (
                        <>
                          <button onClick={handleUpdateDriver} disabled={saving} className="w-7 h-7 flex items-center justify-center rounded-md bg-vip-gold/15 text-vip-gold hover:bg-vip-gold/25 transition-colors">
                            <i className="ri-check-line text-xs"></i>
                          </button>
                          <button onClick={() => setEditDriver(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-white transition-colors">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditDriver(d)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors">
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                          <button onClick={() => setDeleteDriverConfirm(d)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors">
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {drivers.length === 0 && <p className="text-sm text-gray-500 text-center py-4">Henüz şoför eklenmemiş.</p>}
              <p className="text-xs text-gray-500 mt-4">Şoför değişiklikleri anında kaydedilir.</p>
            </div>
          )}

          {/* Save Settings Button (for genel and fiyat tabs) */}
          {aktifSekme !== 'sofor' && (
            <div className="mt-6 flex items-center gap-3">
              <button onClick={handleSaveSettings} disabled={saving}
                className="px-6 py-2.5 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors disabled:opacity-50 whitespace-nowrap">
                <i className="ri-save-line mr-2"></i>{saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}